import { Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from "react-router-dom";
import "@/App.css";
import { DraftGuardProvider } from "./context/DraftGuardContext";
import lazyWithReload from "./lib/lazyWithReload";

// Structural shells stay eager — they wrap (almost) every route, so lazy-loading
// them would just add a spinner with no payoff.
import Layout from "./components/Layout";
import SettingsLayout from "./components/SettingsLayout";
import RequirePerm from "./components/RequirePerm";
import RequireFeature from "./components/RequireFeature";

// Pages are code-split: each becomes its own chunk loaded on demand, which keeps
// heavy deps (recharts, jspdf, html2canvas, …) out of the initial bundle.
const Auth = lazyWithReload(() => import("./pages/Auth"));
const Dashboard = lazyWithReload(() => import("./pages/Dashboard"));
const InvoiceCreate = lazyWithReload(() => import("./pages/InvoiceCreate"));
const InvoiceView = lazyWithReload(() => import("./pages/InvoiceView"));
const Invoices = lazyWithReload(() => import("./pages/Invoices"));
const Estimates = lazyWithReload(() => import("./pages/Estimates"));
const EstimateCreate = lazyWithReload(() => import("./pages/EstimateCreate"));
const EstimateView = lazyWithReload(() => import("./pages/EstimateView"));
const BackupExport = lazyWithReload(() => import("./pages/BackupExport"));
const Clients = lazyWithReload(() => import("./pages/Clients"));
const Products = lazyWithReload(() => import("./pages/Products"));
const Labels = lazyWithReload(() => import("./pages/Labels"));
const Expenses = lazyWithReload(() => import("./pages/Expenses"));
const Payments = lazyWithReload(() => import("./pages/Payments"));
const Taxes = lazyWithReload(() => import("./pages/Taxes"));
const Banks = lazyWithReload(() => import("./pages/Banks"));
const AdminDashboard = lazyWithReload(() => import("./pages/AdminDashboard"));
const AdminBillingSettings = lazyWithReload(() => import("./pages/AdminBillingSettings"));
const SubscriptionDashboard = lazyWithReload(() => import("./pages/SubscriptionDashboard"));
const BusinessSettings = lazyWithReload(() => import("./pages/settings/BusinessSettings"));
const AccountSettings = lazyWithReload(() => import("./pages/settings/AccountSettings"));
const CustomizationSettings = lazyWithReload(() => import("./pages/settings/CustomizationSettings"));
const TeamSettings = lazyWithReload(() => import("./pages/settings/TeamSettings"));
const TariffsSettings = lazyWithReload(() => import("./pages/settings/TariffsSettings"));
const GestorAccessSettings = lazyWithReload(() => import("./pages/settings/GestorAccessSettings"));
const GestorWorkspace = lazyWithReload(() => import("./pages/GestorWorkspace"));
const AdminGestores = lazyWithReload(() => import("./pages/AdminGestores"));
const LandingPage = lazyWithReload(() => import("./pages/LandingPage"));
const Funcionalidades = lazyWithReload(() => import("./pages/Funcionalidades"));
const SistemaVerifactu = lazyWithReload(() => import("./pages/SistemaVerifactu"));
const Calculadoras = lazyWithReload(() => import("./pages/Calculadoras"));
const CalendarioFiscal = lazyWithReload(() => import("./pages/CalendarioFiscal"));
const Privacy = lazyWithReload(() => import("./pages/Privacy"));
const PayInvoice = lazyWithReload(() => import("./pages/PayInvoice"));
const Remittances = lazyWithReload(() => import("./pages/Remittances"));
const Treasury = lazyWithReload(() => import("./pages/Treasury"));
const Reports = lazyWithReload(() => import("./pages/Reports"));
const Orders = lazyWithReload(() => import("./pages/Orders"));
const OrderCreate = lazyWithReload(() => import("./pages/OrderCreate"));
const OrderView = lazyWithReload(() => import("./pages/OrderView"));
const Deliveries = lazyWithReload(() => import("./pages/Deliveries"));
const DeliveryView = lazyWithReload(() => import("./pages/DeliveryView"));
const TrackOrder = lazyWithReload(() => import("./pages/TrackOrder"));
const Stores = lazyWithReload(() => import("./pages/Stores"));
const TPV = lazyWithReload(() => import("./pages/TPV"));
const MaterialsApp = lazyWithReload(() => import("./pages/materials/MaterialsApp"));

// Shown while a route chunk is being fetched.
const PageFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="w-8 h-8 border-2 border-[#1a4f8b] border-t-transparent rounded-full animate-spin" />
  </div>
);

// Force a fresh editor instance per document type / edited id. Without this,
// React Router keeps the same <InvoiceCreate/> mounted when only the ?docType=
// (or ?id=) query changes, so switching factura⇄albarán⇄proforma carries the
// state over and the autosave re-parks the same document under every type's
// draft key — the "one document shows in three sites" bug. Keying by the query
// remounts the editor clean for each document.
function InvoiceCreateRoute() {
  const [sp] = useSearchParams();
  return <InvoiceCreate key={sp.get('id') || sp.get('docType') || 'invoice'} />;
}
function EstimateCreateRoute() {
  const [sp] = useSearchParams();
  return <EstimateCreate key={sp.get('id') || 'new'} />;
}

function App() {
  return (
    <BrowserRouter>
      <DraftGuardProvider>
      <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/auth" element={<Auth />} />
        
        {/* Public Landing Page */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/funcionalidades" element={<Funcionalidades />} />
        <Route path="/sistema-verifactu" element={<SistemaVerifactu />} />
        <Route path="/verifactu" element={<SistemaVerifactu />} />
        <Route path="/calculadoras" element={<Calculadoras />} />
        <Route path="/calendario-fiscal" element={<CalendarioFiscal />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/privacidad" element={<Privacy />} />

        {/* Public customer payment page (no auth) */}
        <Route path="/pay/:token" element={<PayInvoice />} />

        {/* Public live delivery tracking (no auth) */}
        <Route path="/track/:token" element={<TrackOrder />} />
        
        {/* Protected Routes wrapped in Layout */}
        <Route path="/dashboard" element={<Layout><Dashboard /></Layout>} />
        <Route path="/invoices/new" element={<Layout><RequirePerm anyOf={['billing.create', 'billing.edit']}><InvoiceCreateRoute /></RequirePerm></Layout>} />
        <Route path="/invoices/view/:id" element={<Layout><RequirePerm perm="billing.view"><InvoiceView /></RequirePerm></Layout>} />

        <Route path="/invoices" element={<Layout><RequirePerm perm="billing.view"><Invoices /></RequirePerm></Layout>} />
        <Route path="/estimates" element={<Layout><RequirePerm perm="billing.view"><Estimates /></RequirePerm></Layout>} />
        <Route path="/estimates/new" element={<Layout><RequirePerm anyOf={['billing.create', 'billing.edit']}><EstimateCreateRoute /></RequirePerm></Layout>} />
        <Route path="/estimates/view/:id" element={<Layout><RequirePerm perm="billing.view"><EstimateView /></RequirePerm></Layout>} />
        <Route path="/documents/backup" element={<Layout><RequirePerm perm="billing.view"><BackupExport /></RequirePerm></Layout>} />
        <Route path="/clients" element={<Layout><RequirePerm anyOf={['clients.view', 'contacts.viewCustomers']}><Clients initialTab="Clientes" /></RequirePerm></Layout>} />
        <Route path="/suppliers" element={<Layout><RequirePerm anyOf={['contacts.viewSuppliers', 'clients.view']}><Clients initialTab="Proveedores" /></RequirePerm></Layout>} />
        <Route path="/products" element={<Layout><RequirePerm perm="billing.view"><Products /></RequirePerm></Layout>} />
        <Route path="/products/labels" element={<Layout><RequirePerm perm="billing.view"><Labels /></RequirePerm></Layout>} />
        <Route path="/orders" element={<Layout><RequirePerm anyOf={['orders.view', 'orders.create']}><Orders /></RequirePerm></Layout>} />
        <Route path="/orders/new" element={<Layout><RequirePerm perm="orders.create"><OrderCreate /></RequirePerm></Layout>} />
        <Route path="/orders/view/:id" element={<Layout><RequirePerm anyOf={['orders.view', 'orders.create']}><OrderView /></RequirePerm></Layout>} />
        <Route path="/deliveries" element={<Layout><RequirePerm anyOf={['delivery.view', 'delivery.drive']}><Deliveries /></RequirePerm></Layout>} />
        <Route path="/deliveries/view/:id" element={<Layout><RequirePerm anyOf={['delivery.view', 'delivery.drive']}><DeliveryView /></RequirePerm></Layout>} />
        <Route path="/expenses" element={<Layout><Expenses /></Layout>} />
        <Route path="/taxes" element={<Layout><RequirePerm anyOf={['finance.viewTax', 'finance.reports']}><Taxes /></RequirePerm></Layout>} />
        <Route path="/banks" element={<Layout><RequirePerm anyOf={['finance.viewPayments', 'billing.view']}><Banks /></RequirePerm></Layout>} />
        <Route path="/payments" element={<Layout><RequirePerm anyOf={['finance.viewPayments', 'billing.view']}><Payments /></RequirePerm></Layout>} />
        <Route path="/remittances" element={<Layout><RequirePerm anyOf={['finance.viewPayments', 'billing.view']}><Remittances /></RequirePerm></Layout>} />
        <Route path="/treasury" element={<Layout><RequirePerm anyOf={['finance.reports', 'reports.dashboards']}><Treasury /></RequirePerm></Layout>} />
        <Route path="/reports" element={<Layout><RequirePerm anyOf={['reports.dashboards', 'finance.reports']}><Reports /></RequirePerm></Layout>} />
        <Route path="/stores" element={<Layout><Stores /></Layout>} />
        <Route path="/tpv" element={<Layout><RequirePerm anyOf={['billing.create', 'billing.view']}><TPV /></RequirePerm></Layout>} />

        {/* Material Management ERP — gated per client by the material_management feature flag */}
        <Route path="/materials" element={<Layout><RequireFeature feature="material_management"><MaterialsApp /></RequireFeature></Layout>} />
        <Route path="/materials/:moduleKey" element={<Layout><RequireFeature feature="material_management"><MaterialsApp /></RequireFeature></Layout>} />
        <Route path="/admin" element={<Layout><AdminDashboard /></Layout>} />
        <Route path="/admin/billing" element={<Layout><AdminBillingSettings /></Layout>} />
        <Route path="/admin/gestores" element={<Layout><AdminGestores /></Layout>} />
        <Route path="/subscription" element={<Layout><SubscriptionDashboard /></Layout>} />
        
        {/* Settings Routes wrapped in SettingsLayout */}
        <Route path="/settings/business" element={<Layout><SettingsLayout><BusinessSettings /></SettingsLayout></Layout>} />
        <Route path="/settings/account" element={<Layout><SettingsLayout><AccountSettings /></SettingsLayout></Layout>} />
        <Route path="/settings/customization" element={<Layout><SettingsLayout><CustomizationSettings /></SettingsLayout></Layout>} />
        <Route path="/settings/team" element={<Layout><SettingsLayout><TeamSettings /></SettingsLayout></Layout>} />
        <Route path="/settings/tariffs" element={<Layout><SettingsLayout><TariffsSettings /></SettingsLayout></Layout>} />
        <Route path="/settings/gestor" element={<Layout><SettingsLayout><GestorAccessSettings /></SettingsLayout></Layout>} />

        {/* Gestoría workspace (accountant-role users) */}
        <Route path="/gestor" element={<Layout><GestorWorkspace /></Layout>} />

        <Route path="/settings" element={<Navigate to="/settings/business" replace />} />
      </Routes>
      </Suspense>
      </DraftGuardProvider>
    </BrowserRouter>
  );
}

export default App;
